import React, { useEffect, useRef, useState } from 'react';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import { replacePersonalOrderImage } from '@api';

// Mirrors the EditImage modal crop UX for the Personal Area "replace image"
// flow: the new photo is dragged/zoomed inside the order's frame, then the
// square crop (same params as EditImage getCropData) is sent to the server,
// which overwrites the S3 print file while the order is still "נקלט".
const MAX_CROP_SIZE = 2000;
const ZOOM_MAX_FACTOR = 3;

const ReplaceImageModal = ({
  file,          // File chosen by the user
  target,        // { imageName? , imageId? } — the order image being replaced
  frame,         // FRAME_MAP entry for the order's frameType (may be undefined)
  orderId,
  token,
  onClose,
  onReplaced,
}) => {
  const cropperRef = useRef(null);
  const baseZoomRef = useRef(null);
  const [src, setSrc] = useState(null);
  const [naturalSize, setNaturalSize] = useState(null);
  const [zoomFactor, setZoomFactor] = useState(1);
  const [bwFilter, setBwFilter] = useState(false);
  const [effectOpen, setEffectOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!file) return undefined;
    const url = URL.createObjectURL(file);
    setSrc(url);
    const probe = new Image();
    probe.onload = () => setNaturalSize({ width: probe.naturalWidth, height: probe.naturalHeight });
    probe.onerror = () => setError('לא ניתן לקרוא את הקובץ שנבחר, נסו תמונה אחרת');
    probe.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const getCropper = () => cropperRef.current?.cropper;

  const handleReady = () => {
    const cropper = getCropper();
    if (!cropper) return;
    const imageData = cropper.getImageData();
    if (imageData.naturalWidth) {
      baseZoomRef.current = imageData.width / imageData.naturalWidth;
    }
  };

  // Keep the slider in sync with pinch/scroll zoom, and clamp to the range.
  const handleZoomEvent = (event) => {
    const base = baseZoomRef.current;
    if (!base) return;
    const factor = event.detail.ratio / base;
    if (factor < 1 || factor > ZOOM_MAX_FACTOR) {
      event.preventDefault();
      return;
    }
    setZoomFactor(factor);
  };

  const handleSliderChange = (e) => {
    const factor = parseFloat(e.target.value);
    const cropper = getCropper();
    const base = baseZoomRef.current;
    if (!cropper || !base) return;
    setZoomFactor(factor);
    cropper.zoomTo(base * factor);
  };

  const handleSave = async () => {
    const cropper = getCropper();
    if (!cropper || saving) return;
    setSaving(true);
    setError('');
    try {
      const imageData = cropper.getImageData();
      const maxSize = Math.min(
        MAX_CROP_SIZE,
        Math.max(imageData.naturalWidth || MAX_CROP_SIZE, imageData.naturalHeight || MAX_CROP_SIZE)
      );
      const canvas = cropper.getCroppedCanvas({
        width: maxSize,
        height: maxSize,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
      });
      // Raw colour crop — the server bakes greyscale into the print file only,
      // matching the normal edit pipeline.
      const croppedImage = canvas.toDataURL('image/jpeg', 0.85);
      const result = await replacePersonalOrderImage(token, orderId, {
        imageName: target?.imageName,
        imageId: target?.imageId,
        croppedImage,
        bwFilter,
      });
      if (result.success) {
        // Hand the crop back so the grid can swap the photo in place —
        // no refetch (which would re-sign every S3 URL and reload the grid).
        onReplaced({ dataUrl: croppedImage, bwFilter });
      } else {
        setError(result.error || 'שגיאה בהחלפת התמונה, נסו שוב');
        setSaving(false);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בהחלפת התמונה, נסו שוב');
      setSaving(false);
    }
  };

  const lowRes = naturalSize && Math.max(naturalSize.width, naturalSize.height) < 800;

  const photoBox = frame
    ? { left: frame.left, top: frame.top, width: frame.width, height: frame.height }
    : { left: '4%', top: '4%', width: '92%', height: '92%' };

  return (
    <div className="pa-replace__overlay" role="dialog" aria-modal="true" dir="rtl">
      <div className="pa-replace">
        <div className="pa-replace__header">
          <h3 className="pa-replace__title">החלפת תמונה</h3>
          <button
            type="button"
            className="pa-replace__close"
            onClick={onClose}
            disabled={saving}
            aria-label="סגירה"
          >
            ✕
          </button>
        </div>

        <p className="pa-replace__hint">הזיזו והגדילו את התמונה בתוך המסגרת</p>

        <div className="pa-replace__stage">
          <div
            className="pa-replace__frame"
            style={frame ? { backgroundImage: `url(${frame.bg})` } : undefined}
          >
            <div
              className="pa-replace__crop-area"
              style={{ ...photoBox, filter: bwFilter ? 'grayscale(1)' : 'none' }}
            >
              {src && (
                <Cropper
                  ref={cropperRef}
                  src={src}
                  style={{ width: '100%', height: '100%' }}
                  initialAspectRatio={1}
                  aspectRatio={1}
                  viewMode={3}
                  autoCropArea={1}
                  dragMode="move"
                  guides={false}
                  center={false}
                  background={false}
                  responsive
                  checkOrientation
                  toggleDragModeOnDblclick={false}
                  cropBoxMovable={false}
                  cropBoxResizable={false}
                  zoomable
                  ready={handleReady}
                  zoom={handleZoomEvent}
                />
              )}
            </div>
          </div>

        </div>

        <div className="pa-replace__controls">
          <div className="pa-replace__zoom" dir="ltr">
            <span className="pa-replace__zoom-sign">−</span>
            <input
              type="range"
              min="1"
              max={ZOOM_MAX_FACTOR}
              step="0.01"
              value={zoomFactor}
              onChange={handleSliderChange}
              aria-label="זום"
            />
            <span className="pa-replace__zoom-sign">+</span>
          </div>
          <button
            type="button"
            className={`pa-replace__fx${effectOpen ? ' on' : ''}`}
            onClick={() => setEffectOpen((v) => !v)}
          >
            <svg viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 3a9 9 0 000 18z" className="pa-replace__fx-half" />
            </svg>
            <span>אפקט</span>
          </button>
        </div>

        {/* Effect picker — original / B&W preview cards, wall-style */}
        {effectOpen && src && (
          <div className="pa-replace__fxcards">
            <button
              type="button"
              className={`pa-replace__fxcard${!bwFilter ? ' sel' : ''}`}
              onClick={() => setBwFilter(false)}
            >
              <img className="pa-replace__fxprev" src={src} alt="" />
              <span>מקורי</span>
            </button>
            <button
              type="button"
              className={`pa-replace__fxcard${bwFilter ? ' sel' : ''}`}
              onClick={() => setBwFilter(true)}
            >
              <img className="pa-replace__fxprev pa-replace__fxprev--bw" src={src} alt="" />
              <span>שחור לבן</span>
            </button>
          </div>
        )}

        {lowRes && (
          <p className="pa-replace__warning">
            שימו לב: רזולוציית התמונה נמוכה וייתכן שאיכות ההדפסה תיפגע
          </p>
        )}
        {error && <p className="pa-replace__error">{error}</p>}

        <div className="pa-replace__actions">
          <button
            type="button"
            className="pa-replace__save"
            onClick={handleSave}
            disabled={saving || !src}
          >
            {saving ? 'מעלים את התמונה...' : 'שמירת התמונה החדשה'}
          </button>
          <button
            type="button"
            className="pa-replace__cancel"
            onClick={onClose}
            disabled={saving}
          >
            ביטול
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReplaceImageModal;
